import React, { useRef, useEffect } from 'react';
import ImageGallery from 'react-image-gallery';
import { MdArrowBack, MdArrowForward, MdOpenInNew } from 'react-icons/md';

import { cn } from '../../../tailwind-base/utils';
import { Button } from '../../shadcn/Button';
import Link from '../Link';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { shrinkProductImage } from '@repo/utils';
import { useCarouselDefaultProps, CarouselProps } from './props.types';

const isBrowser = typeof window !== 'undefined';

export const MobileImageCarousel = (props: Readonly<CarouselProps>) => {
  const CarouselDefaultProps = useCarouselDefaultProps();

  const images = props?.images ?? CarouselDefaultProps.images;
  const selectedMedia =
    props?.selectedMedia ?? CarouselDefaultProps.selectedMedia;
  const setSelectedMedia =
    props?.setSelectedMedia ?? CarouselDefaultProps.setSelectedMedia;

  const imageGallery = useRef<ImageGallery>();
  const { tt } = useUiComponentsInternationalContext();

  useEffect(() => {
    if (imageGallery.current) {
      imageGallery.current.slideToIndex(
        images?.findIndex(ig => ig.id === selectedMedia?.id)
      );
    }
  }, [images, selectedMedia]);

  if (!isBrowser) return null;

  return (
    <div className="lg:hidden" id="mobile_image_gallery">
      <div className="relative max-h-[620px] overflow-hidden">
        {!!selectedMedia?.image?.src && (
          <Link
            className="absolute bottom-2 right-2 rounded-full z-[2]"
            link={selectedMedia?.image.src}
          >
            <Button
              aria-label={tt(
                s => s.productContainerDeptComponents.fullScreenAriaLabel
              )}
              className="w-9 py-2"
              size="sm"
              variant="ghost"
            >
              <MdOpenInNew className="min-w-6 min-h-6" />
            </Button>
          </Link>
        )}
        <ImageGallery
          items={images.map((ig, index) => ({
            alt: ig.image.altText,
            index,
            original: shrinkProductImage(ig.image.src, 620),
            originalClass: 'w-[90%] h-[90%]',
          }))}
          lazyLoad
          onSlide={ix => setSelectedMedia(images[ix])}
          renderItem={item => (
            <img
              alt={item.id || ''}
              className="cursor-auto rounded-lg"
              fetchPriority={item.index === 0 ? 'high' : 'auto'}
              loading="lazy"
              src={item.original}
            />
          )}
          renderLeftNav={(onClick, disabled) => (
            <Button
              aria-label={tt(
                s => s.productContainerDeptComponents.previousAriaLabel
              )}
              className="absolute w-9 py-2 left-4 top-[40%] rounded-full border border-input z-10"
              disabled={disabled}
              onClick={onClick}
              size="sm"
              variant="ghost"
            >
              <MdArrowBack className="min-w-6 min-h-6" />
            </Button>
          )}
          renderRightNav={(onClick, disabled) => (
            <Button
              aria-label={tt(
                s => s.productContainerDeptComponents.nextAriaLabel
              )}
              className="absolute w-9 py-2 right-4 top-[40%] rounded-full border border-input z-10"
              disabled={disabled}
              onClick={onClick}
              size="sm"
              variant="ghost"
            >
              <MdArrowForward className="min-w-6 min-h-6" />
            </Button>
          )}
          ref={imageGallery}
          showFullscreenButton={false}
          showNav
          showPlayButton={false}
          slideDuration={200}
        />
      </div>
      <div className="flex gap-4 py-2 mt-2 items-center justify-center ">
        {images.map(m => (
          <div
            className={cn(
              'w-5 rounded-full cursor-pointer',
              selectedMedia?.id === m.id ? 'h-1 bg-brand' : 'h-0.5 bg-accent'
            )}
            key={m.id}
            onClick={() => setSelectedMedia(m)}
          />
        ))}
      </div>
    </div>
  );
};
